/**
 * SerpAPI Adapter
 * Implementation for SerpAPI (Google/Bing scraping service)
 * https://serpapi.com
 */

import type { IWebSearchAdapter, WebSearchOptions } from './IWebSearchAdapter';
import type {
  SerpApiSearchConfig,
  WebSearchResponse,
  WebSearchResult,
  RateLimitInfo,
} from '@/types/webSearch.types';
import { getBackendUrl, getAuthHeaders, prepareWebSearchQuery } from './webSearchUtils';

interface SerpApiOrganicResult {
  title?: string;
  link?: string;
  snippet?: string;
  date?: string;
  position?: number;
}

interface SerpApiResponse {
  organic_results?: SerpApiOrganicResult[];
}

export class SerpApiAdapter implements IWebSearchAdapter {
  public readonly provider = 'serpapi';
  private config: SerpApiSearchConfig | null = null;

  /**
   * Initialize the SerpAPI adapter with configuration
   */
  async initialize(config: SerpApiSearchConfig): Promise<void> {
    if (!config.apiKey || config.apiKey.trim() === '') {
      throw new Error('SerpAPI API key is required');
    }

    if (config.provider !== 'serpapi') {
      throw new Error(`Invalid provider: expected 'serpapi', got '${config.provider}'`);
    }

    this.config = config;
  }

  /**
   * Execute a web search using SerpAPI via backend proxy
   */
  async search(query: string, options?: WebSearchOptions): Promise<WebSearchResponse> {
    if (!this.config) {
      throw new Error('Adapter not initialized. Call initialize() first.');
    }

    if (!query || query.trim() === '') {
      throw new Error('Search query cannot be empty');
    }

    const startTime = Date.now();

    try {
      // Call backend proxy to avoid CORS issues
      const response = await fetch(`${getBackendUrl()}/web-search`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          provider: 'serpapi',
          query: prepareWebSearchQuery(query, 'serpapi'),
          originalQueryLength: query.trim().length,
          config: {
            apiKey: this.config.apiKey,
            engine: this.config.engine || 'google',
            maxResults: this.config.maxResults || 10,
            location: this.config.location,
          },
        }),
        signal: options?.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `SerpAPI error (${response.status}): ${errorText || response.statusText}`
        );
      }

      const data = await response.json() as SerpApiResponse;

      // Normalize SerpAPI response to our standard format
      const organicResults = data.organic_results || [];
      const results: WebSearchResult[] = organicResults.map((result: SerpApiOrganicResult) => ({
        title: result.title || '',
        url: result.link || '',
        snippet: result.snippet || '',
        publishedDate: result.date ? new Date(result.date) : undefined,
        relevanceScore: result.position ? 1 / result.position : undefined, // Lower position = higher relevance
      }));

      return {
        query,
        results,
        totalResults: results.length,
        metadata: {
          provider: 'serpapi',
          took: Date.now() - startTime,
          cached: false,
        },
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`SerpAPI search failed: ${error.message}`);
      }
      throw new Error('SerpAPI search failed with unknown error');
    }
  }

  /**
   * Test connection by performing a simple search
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.search('test connection');
      return true;
    } catch (error) {
      console.error('SerpAPI connection test failed:', error);
      return false;
    }
  }

  /**
   * SerpAPI provides account info but not rate limits in search response
   */
  async getRateLimitInfo(): Promise<RateLimitInfo | null> {
    // Could be implemented by calling /account endpoint
    return null;
  }
}
